import { ACCENT_STYLES } from '../accents';
import { Favicon } from './Favicon';
import type { System } from '../api';

interface Props {
  system: System;
  index: number;
  total: number;
}

export function SystemTile({ system, index, total }: Props) {
  const accent = ACCENT_STYLES[system.accent] ?? ACCENT_STYLES.slate;

  return (
    <a
      href={system.url}
      target="_blank"
      rel="noreferrer"
      draggable={false}
      className="group relative flex h-full flex-col overflow-hidden rounded-sm border-2 hair-strong bg-ink-700 shadow-[inset_0_1px_0_rgba(244,236,221,0.06),0_8px_24px_-14px_rgba(0,0,0,0.7)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-cream-300/40 hover:bg-ink-600 hover:shadow-[inset_0_1px_0_rgba(244,236,221,0.10),0_18px_38px_-18px_rgba(0,0,0,0.85)]"
    >
      {/* Thick left strip — the primary visual cue per accent. */}
      <span
        className={`absolute inset-y-0 left-0 w-2 ${accent.strip} transition-all duration-300 group-hover:w-3`}
        aria-hidden
      />

      {/* Top-edge highlight — gives the card a "lit from above" feel. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cream/20 to-transparent"
      />

      {/* Soft accent halo that intensifies on hover. */}
      <span
        className="pointer-events-none absolute -left-10 -top-10 h-44 w-44 rounded-full opacity-30 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: `radial-gradient(circle, ${accent.hex}33 0%, transparent 65%)`,
        }}
        aria-hidden
      />

      <div className="relative flex h-full flex-col gap-4 pl-8 pr-6 pt-6">
        {/* Header: favicon + meta */}
        <div className="flex items-start justify-between gap-4">
          <Favicon
            url={system.url}
            name={system.name}
            icon={system.icon}
            accent={system.accent}
            size={14}
          />
          <div className="flex flex-col items-end gap-1.5 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
            <span>
              № {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
            <span className="flex items-center gap-2">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${accent.dot}`} />
              Live
            </span>
          </div>
        </div>

        <div className="pt-1">
          <h3 className="font-display text-[24px] font-medium leading-tight tracking-tightish text-cream transition-colors group-hover:text-coral-300">
            {system.name}
          </h3>
          <div className="mt-1 font-mono text-[11px] lowercase tracking-wide text-cream-500">
            {hostnameOf(system.url)}
          </div>
        </div>

        <p className="text-[13.5px] leading-relaxed text-cream-700 text-pretty">
          {system.description || <span className="italic text-cream-300">No description.</span>}
        </p>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between border-t hair pb-5 pt-4">
          <span
            className={`font-mono text-[10px] uppercase tracking-widestest ${accent.chip} rounded-sm px-2 py-1`}
          >
            {system.accent}
          </span>
          <span className="flex items-center gap-2 rounded-sm border hair px-2.5 py-1 font-mono text-[11px] uppercase tracking-widestest text-cream-700 transition-all duration-300 group-hover:border-coral group-hover:bg-coral group-hover:text-ink">
            Open
            <span
              aria-hidden
              className="inline-block translate-x-0 transition-transform duration-300 group-hover:translate-x-0.5"
            >
              ↗
            </span>
          </span>
        </div>
      </div>
    </a>
  );
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
